import { BaseClient, normalizeQuery } from '../base-client';
import type { ExportCustomersQuery } from '../customers/types';
import type {
  CreateSowInput,
  Engagement,
  ImportSowResult,
  ListSowsQuery,
  PatchSowInput,
  ReplaceSowInput,
  Sow,
  SowWithEngagementSummary,
} from './types';

type SowWithEngagements = { data: Sow; related: { engagements: Engagement[] } };
type SowOnly = { data: Sow };
type ListResponse = { data: Sow[]; meta: { total: number; limit: number; offset: number } };
type AllVersionsResponse = Omit<ListResponse, 'data'> & { data: SowWithEngagementSummary[] };
type CountResponse = { data: { total: number } };
type ImportResponse = { data: ImportSowResult };

export class SowsClient extends BaseClient {
  list(query: ListSowsQuery = {}): Promise<ListResponse> {
    return this.request('/api/sows', { query: normalize(query) });
  }

  listLatest(query: ListSowsQuery = {}): Promise<ListResponse> {
    return this.request('/api/sows/latest', { query: normalize(query) });
  }

  listAllVersions(query: ListSowsQuery = {}): Promise<AllVersionsResponse> {
    return this.request('/api/sows/all-versions', { query: normalize(query) });
  }

  count(
    query: ListSowsQuery & { mode?: 'in_effect' | 'latest' | 'all' } = {},
  ): Promise<CountResponse> {
    return this.request('/api/sows/count', { query: normalize(query) });
  }

  get(id: string, asOf?: string): Promise<SowWithEngagements> {
    return this.request(`/api/sows/${encodeURIComponent(id)}`, {
      query: asOf ? { asOf } : undefined,
    });
  }

  versions(id: string): Promise<{ data: Sow[]; meta: { total: number } }> {
    return this.request(`/api/sows/${encodeURIComponent(id)}/versions`);
  }

  getVersion(id: string, version: number): Promise<SowWithEngagements> {
    return this.request(
      `/api/sows/${encodeURIComponent(id)}/versions/${encodeURIComponent(String(version))}`,
    );
  }

  create(input: CreateSowInput): Promise<SowWithEngagements> {
    return this.request('/api/sows', { method: 'POST', body: input });
  }

  replace(id: string, input: ReplaceSowInput): Promise<SowWithEngagements> {
    return this.request(`/api/sows/${encodeURIComponent(id)}`, { method: 'PUT', body: input });
  }

  patch(id: string, input: PatchSowInput): Promise<SowWithEngagements> {
    return this.request(`/api/sows/${encodeURIComponent(id)}`, { method: 'PATCH', body: input });
  }

  remove(id: string, expectedVersion: number): Promise<SowOnly> {
    return this.request(`/api/sows/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      body: { expectedVersion },
    });
  }

  /**
   * Download SOWs as a four-sheet .xlsx: the agreements, then their
   * retainers, T&M engagements and metric-priced engagements, one row each.
   *
   * Filtered by CUSTOMER, not by SOW — agreements are reached from the
   * customers list, and this mirrors what is on screen there. Pass `{}` for
   * every customer.
   */
  exportFile(
    customerFilter: ExportCustomersQuery = {},
  ): Promise<{ blob: Blob; filename: string | null }> {
    return this.requestBlob('/api/sows/export', { query: normalizeQuery(customerFilter) });
  }

  /** The same four sheets with no rows. A template that differed from an
   *  export would be a second format to keep in step. */
  exportTemplate(): Promise<{ blob: Blob; filename: string | null }> {
    return this.requestBlob('/api/sows/import/template');
  }

  /**
   * Upload a workbook. Each SOW is applied WHOLE — its engagements ride the
   * same write — and one that already matches the file is left alone rather
   * than amended. A SOW absent from the file is untouched: omitting one never
   * ends it.
   *
   * Best-effort per agreement: a failing engagement takes its own SOW down
   * and leaves the rest of the file to apply.
   */
  importFile(file: File, opts: { idempotencyKey?: string } = {}): Promise<ImportResponse> {
    const fd = new FormData();
    fd.append('file', file, file.name);
    const headers: Record<string, string> = {};
    if (opts.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey;
    return this.requestMultipart('/api/sows/import', fd, { headers });
  }

  restore(id: string, expectedVersion: number): Promise<SowWithEngagements> {
    return this.request(`/api/sows/${encodeURIComponent(id)}/restore`, {
      method: 'POST',
      body: { expectedVersion },
    });
  }
}

function normalize(q: object): Record<string, string | number | boolean | undefined> {
  const out: Record<string, string | number | boolean | undefined> = {};
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === '') continue;
    if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') {
      out[k] = v;
    }
  }
  return out;
}
